import CaptchaInitializer from "../../../Scalesoft.Shared.FrontEnd/Scripts/Captcha/CaptchaInitializer";
import {NotificationType} from "../../../Scalesoft.Shared.FrontEnd/Scripts/Enums/NotificationType";
import ErrorHandler from "../../../Scalesoft.Shared.FrontEnd/Scripts/Error-Handler/ErrorHandler";
import {KeyValuePair} from "../../../Scalesoft.Shared.FrontEnd/Scripts/Global/KeyValuePair";
import GuiCommonShared from "../../../Scalesoft.Shared.FrontEnd/Scripts/GuiCommonShared";
import NotificationConfiguration from "../../../Scalesoft.Shared.FrontEnd/Scripts/NotificationConfiguration";
import {MethodContactType} from "../Enums/MethodContactType";
import ContactApiClient from "./ContactApiClient";
import ContactUtils from "./ContactUtils";
import IUserInfoConfirmationResultContract from "./Interfaces/IUserInfoConfirmationResultContract";

// TODO Reimplement this class to match new contact verification view
export default class ContactValidation {
    private readonly validatedInformation: string;
    private readonly contactId: number;
    private readonly verificationCallback: (oldId: number, newId: number, confirmCode: string, response: JQuery.jqXHR) => void;

    // Button for re-sending verification code
    private readonly resendButton: JQuery;

    private readonly contactVerificationForm: JQuery;

    private readonly userId: string;

    private readonly verifyContactModal: JQuery;

    private readonly alertContainer: JQuery;

    private readonly contactVerifiedMessage: JQuery;
    private readonly codeResentMessage: JQuery;

    private readonly contactVerificationRecaptcha: JQuery;

    private readonly verificationCodeInput: JQuery;

    private readonly errorHandler: ErrorHandler;
    private readonly guiCommon: GuiCommonShared;
    private readonly mLocalization: Localization;

    private readonly contactApiClient: ContactApiClient;
    private readonly captchaInitializer: CaptchaInitializer;
    private readonly sessionId: string;

    constructor(validatedInformation: string,
                contactId: number,
                verificationCallback: (oldId: number, newId: number, confirmCode: string, response: JQuery.jqXHR) => void,
                captchaInitializer: CaptchaInitializer,
                guiCommon: GuiCommonShared,
                errorHandler: ErrorHandler,
                localization: Localization,
    ) {
        this.validatedInformation = validatedInformation;
        this.contactId = contactId;
        this.verificationCallback = verificationCallback;
        this.alertContainer = jq(".contact-verification-alerts");
        this.verifyContactModal = jq("#verifyContactModal");

        this.resendButton = jq(`.resend-code-button`);
        this.contactVerificationForm = jq(`#contactVerificationForm`);
        this.userId = jq(`#userId`).val() as string;

        this.contactVerifiedMessage = jq(`#verified-message`);
        this.codeResentMessage = jq(`#resend-code-message`);

        this.contactVerificationRecaptcha = jq(`#verification-recaptcha`);

        this.verificationCodeInput = jq(`#verificationCode`);

        this.captchaInitializer = captchaInitializer;
        this.errorHandler = errorHandler;
        this.guiCommon = guiCommon;
        this.mLocalization = localization;

        this.contactApiClient = new ContactApiClient();
    }

    public main(): void {
        const captchaContainerJq = this.verifyContactModal;

        // TODO do not duplicate event listeners, pass modal from contact list, init shared listeners in contactList class #IN2-5847
        // event listeners are removed as a workaround

        this.verifyContactModal.off("shown.bs.modal");
        this.verifyContactModal.on("shown.bs.modal", () => {
            this.verifyContactModal.find("#verificationCode").trigger("focus");
            this.captchaInitializer.prepareContainer(jq, captchaContainerJq);
        });
        this.verifyContactModal.off("hide.bs.modal");
        this.verifyContactModal.on("hide.bs.modal", () => {
            this.captchaInitializer.cleanupContainer(captchaContainerJq);
        });

        this.verifyContactModal.off("click");
        this.verifyContactModal.on("click", () => {
            this.guiCommon.destroyNotification(this.alertContainer);
            this.codeResentMessage.hide();
        });

        this.resendButton.off("click");
        this.resendButton.on("click", (event) => {
            event.preventDefault();

            this.codeResentMessage.hide();
            this.sendResendCodeRequest(captchaContainerJq);
        });

        this.contactVerificationForm.off("submit");
        this.contactVerificationForm.on("submit", (event) => {
            event.preventDefault();

            this.sendVerificationRequest(captchaContainerJq, this.sessionId);
        });

        if (this.contactApiClient.getContactType() === MethodContactType.Permanent) {
            this.contactApiClient.ensureCodeHasBeenSent(this.contactId)
                .fail((response) => {
                    this.onFail(response, this.alertContainer);
                });
        }
    }

    private onFail(response: JQuery.jqXHR, errorMessageElement: JQuery): void {
        this.resetForm();

        const notificationConfig = new NotificationConfiguration({
            notificationType: NotificationType.Danger,
            messages: this.errorHandler.getErrorText(response),
            container: errorMessageElement,
        });

        this.guiCommon.showNotification(notificationConfig);
    }

    private sendResendCodeRequest(captchaContainerJq: JQuery): void {
        const captchaCallback = (captchaParamsInner: Array<KeyValuePair<string, string>>) => {
            ContactUtils.handleLoading(this.verifyContactModal, true, ".resend-loading", ".resend-code-button");

            const resendCode =
                this.contactApiClient.resendCode(
                    this.contactId,
                    captchaParamsInner);

            resendCode.done((response) => {
                if (response) {
                    this.onResendCodeDone();
                } else {
                    this.mLocalization.translateAsync("generic-error", "remote-error")
                        .then((genericErrorTranslation) => {
                            this.alertContainer
                                .html(genericErrorTranslation.result.value);
                        });
                }
            });

            resendCode.fail((response) => {
                this.onFail(response, this.alertContainer);
                this.captchaInitializer.prepareContainer(jq, captchaContainerJq);
            });

            resendCode.always(() => {
                ContactUtils.handleLoading(this.verifyContactModal, false, ".resend-loading", ".resend-code-button");
            });
        };

        this.captchaInitializer.initForAjax(jq, captchaContainerJq, captchaCallback, "resendCode");
    }

    private onResendCodeDone(): void {
        this.resetForm();

        this.codeResentMessage.show();
        return;
    }

    private sendVerificationRequest(captchaContainerJq: JQuery, sessionId: string = null): void {
        const inputVerificationCode = this.verificationCodeInput.val() as string;

        const captchaCallback = (captchaParams: Array<KeyValuePair<string, string>>) => {
            ContactUtils.handleLoading(this.verifyContactModal, true, ".submit-loading", ".submit-modal-form-btn");

            const verifyContact = this.contactApiClient.verifyContact(
                this.contactId,
                inputVerificationCode,
                captchaParams,
            );

            verifyContact.always(() => {
                ContactUtils.handleLoading(this.verifyContactModal, false, ".submit-loading", ".submit-modal-form-btn");
            });

            verifyContact.done((response: IUserInfoConfirmationResultContract) => {
                if (response.isConfirmed) {
                    this.onVerificationDone(response.id, inputVerificationCode, response);
                    this.verifyContactModal.modal("hide");
                    jq(document.documentElement).trigger("contacts-changed");
                } else {
                    this.mLocalization.translateAsync("confirm-code-no-match", "UserInput")
                        .then((noMatchTranslation) => {
                            this.guiCommon.showNotification(new NotificationConfiguration({
                                container: this.alertContainer,
                                notificationType: NotificationType.Danger,
                                messages: [noMatchTranslation.result.value],
                            }));
                        });
                }
            });

            verifyContact.fail((response) => {
                this.onFail(response, this.alertContainer);
                this.captchaInitializer.prepareContainer(jq, captchaContainerJq);
            });
        };

        this.captchaInitializer.initForAjax(jq, captchaContainerJq, captchaCallback, "verifyContact");
    }

    private onVerificationDone(newId: number, confirmCode: string, response): void {
        this.resetForm();
        this.verificationCallback(this.contactId, newId, confirmCode, response);
    }

    private hideMessages(): void {
        this.codeResentMessage.hide();
        this.contactVerifiedMessage.hide();
    }

    private clearErrors(): void {
        this.guiCommon.destroyNotification(this.alertContainer);
    }

    private resetForm(): void {
        this.hideMessages();
        this.clearErrors();
    }
}
